// [목적] 알림 표시 로직 공용 모듈 — 문구 조립/라우팅 경로/상대시간 + 맞팔로우 버튼 + 알림 행 컴포넌트
// [이유] 2026-09-20 notification-bell: 알림을 TopBar 벨 드롭다운에서도 보여주게 되면서, 기존에
//        NotificationsPage.tsx 안에만 private 으로 있던 buildNotificationText/buildTargetPath/
//        formatRelativeTime/FollowBackButton/NotificationItem 을 두 화면이 함께 써야 한다.
//        복사본을 만들면 "문구는 페이지에서만 고쳐지고 드롭다운은 옛 문구를 계속 보여주는" 류의
//        드리프트가 확정적으로 생기므로 단일 소스로 추출한다(ADR-T3-3 문구 규칙도 여기 한 곳에만 존재).
// [사이드 이팩트] NotificationsPage.tsx 는 이 모듈을 import 해서 쓰고, 동일 함수를 자체 보유하지 않는다.
//                 NotificationRow 는 density='page' | 'panel' 두 밀도만 지원한다(그 외 변형 추가 금지 —
//                 늘어나면 화면별 분기가 다시 갈라진다).
// [수정 시 고려사항] NotificationItem 의 type 이 추가되면 buildNotificationText 의 switch 와
//                   AVATAR_BG_BY_TYPE 를 반드시 동시 갱신할 것(둘 중 하나만 고치면 새 타입이
//                   기본 브랜드색 + "새 알림이 있어요" 로 조용히 뭉개진다).
//                   ADR-T3-3: 소셜 알림 문구는 FE 조립, system/badge 는 BE content 우선 — 이 규칙 유지.

import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { tokens } from '@gangwoncanvas/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import type { NotificationItem } from '@gangwoncanvas/types';

import { follow } from '../../api/user.client.js';
import { useToast } from '../feedback/Toast.js';
import { useAuth } from '../../hooks/useAuth.js';

const c = tokens.color;
const L = 'light' as const;
const TEXT_HI = c.ink.high[L];
const TEXT_MID = c.ink.mid[L];
const TEXT_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const SOFT_LINE = c.line.soft[L];
const PAPER0 = c.paper[0][L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];

/**
 * [목적] 미읽 알림 행 배경 — paper[0] 위에서 "안 읽음"이 한눈에 구분되는 연한 브랜드 틴트
 * [이유] 기존 rgba(31,111,235,.04) 는 편지지 톤 배경에서 거의 식별되지 않았다.
 * [사이드 이팩트] 없음(상수).
 * [수정 시 고려사항] 더 진하게 바꾸면 그 위 본문(ink.high) 대비를 재측정할 것.
 */
const UNREAD_BG = alpha(c.brand[500][L], 0.055);
const UNREAD_BG_HOVER = alpha(c.brand[500][L], 0.095);

/**
 * [목적] 알림 타입별 아바타 배경색 — 토큰 팔레트 값만 사용
 * [이유] 타입을 색으로 먼저 구분시켜 스캔 속도를 높인다(like=빨강, fork=초록, follow=주황, 그 외 브랜드).
 * [수정 시 고려사항] NotificationItem['type'] 에 값이 추가되면 여기에도 매핑을 추가할 것.
 */
function avatarBgByType(type: NotificationItem['type']): string {
  switch (type) {
    case 'like':
      return c.danger[500][L];
    case 'fork':
      return c.accent[500][L];
    case 'follow':
      return c.warm[500][L];
    default:
      return BRAND; // comment / system / badge
  }
}

/**
 * [목적] 소셜 알림 문구를 FE 에서 type+actor_handle+target 으로 조립 (ADR-T3-3)
 * [이유] i18n·문구 변경을 FE 배포로 유연하게 처리. 소셜 알림은 BE content=NULL.
 *         system/badge 알림은 BE content(BE 문구) 우선 표시.
 * [사이드 이팩트] actor_handle 이 null 인 경우 "알 수 없는 사용자" 폴백(탈퇴 actor graceful).
 * [수정 시 고려사항] type 추가 시 switch 케이스 동기 갱신. i18n 도입 시 헬퍼로 분리.
 */
export function buildNotificationText(item: NotificationItem): {
  actor: string;
  body: string;
  emoji: string;
} {
  const actor = item.actorHandle ? `@${item.actorHandle}` : '알 수 없는 사용자';

  // system/badge: BE content 우선
  if (item.type === 'system' || item.type === 'badge') {
    return {
      actor: '강원캔버스',
      // [2026-09-20] body 앞 공백 1칸 — 호출부가 <strong>{actor}</strong><span>{body}</span> 로 이어 붙여
      //              렌더하므로 공백이 없으면 '강원캔버스새 알림이 있어요' 처럼 단어가 붙어버린다.
      //              소셜 문구는 '님이~' 로 시작해 공백이 없어야 맞으므로 system/badge 에만 붙인다.
      body: ` ${item.content ?? '새 알림이 있어요'}`,
      emoji: item.type === 'badge' ? '🏆' : '📰',
    };
  }

  // 소셜: FE 조립 (ADR-T3-3)
  switch (item.type) {
    case 'like':
      return { actor, body: '님이 회원님의 폴리오를 좋아해요', emoji: '❤️' };
    case 'fork':
      return { actor, body: '님이 회원님의 폴리오를 Fork했어요', emoji: '⑂' };
    case 'follow':
      return { actor, body: '님이 회원님을 팔로우합니다', emoji: '+' };
    case 'comment':
      return { actor, body: '님이 댓글을 남겼어요', emoji: '💬' };
    default:
      return { actor, body: item.content ?? '새 알림이 있어요', emoji: '🔔' };
  }
}

/**
 * [목적] target_type + target_id 로 라우팅 경로 결정
 * [이유] ADR-T3-3: 알림 클릭 시 대상 엔티티 페이지로 이동.
 *         M7 P2: comment 알림은 targetType='folio'(BE folio-comments.service.ts — targetId=folioId)로 온다.
 *         type==='comment' 인 경우 /folio/:id#comments 해시를 추가해 "댓글로 이동"을 명시한다.
 * [수정 시 고려사항] target_type 추가 시 케이스 동기 갱신.
 */
export function buildTargetPath(item: NotificationItem, myHandle?: string | null): string | null {
  // badge 알림은 target_id/target_type 이 항상 null(엔티티가 아니라 "내 프로필 뱃지 탭"이 대상) —
  // 아래 targetId 가드보다 먼저 분기해야 한다. myHandle 없으면(비정상 상황) 이동 안 함.
  if (item.type === 'badge') {
    return myHandle ? `/u/${myHandle}?tab=badges` : null;
  }
  if (!item.targetId || !item.targetType) return null;
  switch (item.targetType) {
    case 'folio':
      return item.type === 'comment'
        ? `/folio/${item.targetId}#comments`
        : `/folio/${item.targetId}`;
    default:
      return null;
  }
}

/**
 * [목적] 생성 시각을 상대 시간 문자열로 변환
 * [이유] 실데이터 ISO 8601 → "5분 전" 형태 표시.
 * [수정 시 고려사항] i18n 도입 시 Intl.RelativeTimeFormat 활용.
 */
export function formatRelativeTime(createdAt: string): string {
  const diff = Date.now() - new Date(createdAt).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return '방금 전';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '어제';
  if (days < 7) return `${days}일 전`;
  return `${Math.floor(days / 7)}주 전`;
}

/**
 * [목적] follow 타입 알림에 노출되는 "맞팔로우" 버튼 — 클릭 시 즉시 follow() 호출
 * [이유] M7 P2 — 팔로우 알림에서 바로 맞팔로우할 수 있는 경로 제공.
 * [사이드 이팩트] 알림 목록 응답에는 isFollowing 상태가 없어 토글이 아닌 단방향 follow 만 지원
 *                (언팔로우는 프로필 화면에서). 성공 후 로컬 state 로 "팔로우 중" 표시 전환(재요청 방지).
 * [수정 시 고려사항] actorHandle 이 null(탈퇴 등)이면 호출부가 렌더 자체를 가드한다.
 */
export function FollowBackButton({ actorHandle }: { actorHandle: string }): JSX.Element {
  const { show } = useToast();
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: () => follow(actorHandle),
    onSuccess: (res) => {
      setDone(true);
      if (!res.alreadyFollowing) show(`@${actorHandle}님을 팔로우했습니다.`, 'success');
    },
    onError: () => show('팔로우 처리 중 오류가 발생했습니다.', 'error'),
  });

  if (done) {
    return (
      <Box
        component="span"
        sx={{ fontSize: 11, color: TEXT_MID, fontWeight: 600, flexShrink: 0, alignSelf: 'center' }}
      >
        팔로우 중
      </Box>
    );
  }

  return (
    <ButtonBase
      onClick={(e) => {
        e.stopPropagation();
        mutation.mutate();
      }}
      disabled={mutation.isPending}
      aria-label={`@${actorHandle} 맞팔로우`}
      sx={{
        // 처리 중에는 opacity 만 낮추지 않고 회색 면으로 바꿔 "지금 못 누름"을 명시한다.
        bgcolor: mutation.isPending ? PAPER2 : BRAND,
        color: mutation.isPending ? TEXT_LOW : PAPER0,
        borderRadius: 99,
        px: '13px',
        py: '7px',
        fontSize: 11.5,
        fontWeight: 700,
        flexShrink: 0,
        alignSelf: 'center',
        whiteSpace: 'nowrap',
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
      }}
    >
      맞팔로우
    </ButtonBase>
  );
}

/**
 * [목적] 알림 단건 행 — 아바타/타입 이모지/문구/시간 + 클릭 시 읽음 처리 + 대상 라우팅
 * [이유] 2026-09-20 notification-bell: 알림 페이지와 TopBar 드롭다운이 같은 행 디자인을 공유해야 한다.
 *         density='page' 는 카드형(둥근 모서리 + 간격), 'panel' 은 드롭다운용 구분선 리스트.
 * [사이드 이팩트] onMarkRead 는 호출부(훅 보유자)가 주입 — 이 컴포넌트는 캐시 무효화를 직접 하지 않는다.
 *                 onNavigate 가 주어지면 라우팅 직전에 호출된다(드롭다운을 닫는 용도).
 * [수정 시 고려사항] 행 컨테이너는 ButtonBase(<button>) 가 아니라 role="button" 의 <div> 여야 한다 —
 *                   follow 알림 안에 진짜 <button>(FollowBackButton)이 중첩되기 때문(HTML 무효 마크업 방지).
 */
export function NotificationRow({
  item,
  density = 'page',
  onMarkRead,
  onNavigate,
}: {
  item: NotificationItem;
  density?: 'page' | 'panel';
  onMarkRead: (id: string) => void;
  onNavigate?: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { actor, body, emoji } = buildNotificationText(item);
  const targetPath = buildTargetPath(item, user?.handle);
  const isUnread = item.readAt === null;
  const isPanel = density === 'panel';

  const handleClick = (): void => {
    onMarkRead(item.id);
    onNavigate?.();
    if (targetPath) navigate(targetPath);
  };

  const avatarSize = isPanel ? 36 : 42;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`${actor} ${body}`}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        gap: isPanel ? '12px' : '13px',
        p: isPanel ? '13px 16px' : '15px 16px',
        position: 'relative',
        textAlign: 'left',
        cursor: 'pointer',
        bgcolor: isUnread ? UNREAD_BG : PAPER0,
        transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
        ...(isPanel
          ? {
              borderBottom: `1px solid ${SOFT_LINE}`,
              '&:last-of-type': { borderBottom: 'none' },
              '&:hover': { bgcolor: isUnread ? UNREAD_BG_HOVER : PAPER1 },
            }
          : {
              border: `1px solid ${isUnread ? alpha(c.brand[500][L], 0.28) : BORDER}`,
              borderRadius: `${tokens.radius.l}px`,
              mb: '10px',
              '&:hover': {
                borderColor: BRAND,
                boxShadow: '0 8px 20px rgba(41,38,27,.09)',
                transform: 'translateY(-1px)',
              },
            }),
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: -2 },
      }}
    >
      {/* 미읽 도트 */}
      {isUnread && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 5,
            height: 5,
            borderRadius: '50%',
            bgcolor: BRAND,
          }}
        />
      )}

      {/* 아바타 + 타입 이모지 */}
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Box
          aria-hidden="true"
          sx={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: '50%',
            bgcolor: avatarBgByType(item.type),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: PAPER0,
            fontSize: isPanel ? 13 : 15,
            fontWeight: 800,
          }}
        >
          {item.type === 'system' || item.type === 'badge'
            ? '✦'
            : (item.actorHandle?.charAt(0).toUpperCase() ?? '?')}
        </Box>
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            width: isPanel ? 18 : 20,
            height: isPanel ? 18 : 20,
            borderRadius: '50%',
            bgcolor: PAPER0,
            border: `2px solid ${PAPER0}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isPanel ? 10 : 11,
            boxShadow: tokens.shadow.card,
          }}
        >
          {emoji}
        </Box>
      </Box>

      {/* 텍스트 영역 */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: isPanel ? 12.5 : 13.5, color: TEXT_HI, lineHeight: 1.5 }}>
          <strong style={{ fontWeight: 700 }}>{actor}</strong>
          <span style={{ color: TEXT_MID, fontWeight: 500 }}>{body}</span>
        </Box>
        <Typography
          sx={{
            fontSize: isPanel ? 10.5 : 11,
            color: TEXT_LOW,
            mt: '4px',
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
          }}
        >
          {formatRelativeTime(item.createdAt)}
        </Typography>
      </Box>

      {/* follow 알림 전용 맞팔로우 버튼 */}
      {item.type === 'follow' && item.actorHandle && (
        <FollowBackButton actorHandle={item.actorHandle} />
      )}
    </Box>
  );
}
